import React, {
    ComponentPropsWithRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { AsgOrPlaceholder, Assignation } from '../context/types'
import { StatementCard } from './StatementCard'
import {
    DragDropContext,
    Draggable,
    Droppable,
    OnDragEndResponder,
} from 'react-beautiful-dnd'
import { reorder } from '../utils'
import Typography from '@mui/material/Typography'
import { Divider, Snackbar } from '@mui/material'
import { Icons } from '../assets/icons'
import { useCommonCtx } from '../context/useCommonCtx'
import { toBoldStrings, useTranslation } from '../i18n/useTranslation'
import { ScaleHint } from './ScaleHint'
import { info } from '../services/sentryLogger'

interface Props extends ComponentPropsWithRef<'div'> {
    stmsOrPlaceholders: AsgOrPlaceholder[]
    stms2Place: Assignation[]
    stmId2Highlight?: string
    submit: (data: Assignation[]) => void
}
export const PlaceStms = (p: Props) => {
    const { className, ...rest } = p
    const [items, setItems] = useState<AsgOrPlaceholder[]>([])
    const [items2place, setItems2Place] = useState<Assignation[]>([])
    const [snackTxt, setSnackTxt] = useState('')
    const commons = useCommonCtx()
    const params = commons.state.params;
    const t = useTranslation(commons)

    useEffect(() => {
        setItems2Place([...p.stms2Place])
        setItems([...p.stmsOrPlaceholders])
    }, [p.stmsOrPlaceholders])

    const allPlaced = useMemo(
        () => items2place.length === 0 && items.length,
        [items2place.length, items.length]
    )
    const onSubmit = useCallback(() => {
        if (allPlaced && p.submit) {
            info('PlaceStms submit', { items })
            setSnackTxt(t('tip_saved'))
            p.submit(items as Assignation[])
        } else {
            info('PlaceStms not allPlaced')
        }
    }, [allPlaced, p.submit])

    useEffect(() => {
        onSubmit()
    }, [onSubmit])

    const onDragEnd: OnDragEndResponder = ({
        source,
        destination,
        combine,
    }) => {
        // info('DraggableList onDragEnd', { source, destination, combine })
        if (!destination) {
            return
        }
        const { droppableId, index: destIdx } = destination
        const { index: sourceIdx } = source ?? {}
        if (droppableId?.startsWith('placeholder')) {
            const sourceItem = items2place[sourceIdx]
            const destIdx = parseInt(droppableId.split('-')[1])
            info('placeholder detected', { sourceItem, destIdx })
            setItems((prevItems) => {
                const newItems = [...prevItems]
                const deletedItems = newItems.splice(destIdx, 1, sourceItem)
                info('newItems', { prevItems, newItems })
                return newItems
            })
            setItems2Place((prevItems2Place) => {
                const newItems2Place = [...prevItems2Place]
                const deletedItems = newItems2Place.splice(sourceIdx, 1)
                info('newItems2Place', {
                    prevItems2Place,
                    newItems2Place,
                })
                return newItems2Place
            })
        } else {
            setItems2Place((prevItems) =>
                reorder([...prevItems], sourceIdx, destIdx ?? -1)
            )
        }

        if (combine) {
            info('combine')
            setItems((prevItems) => {
                const newItems = [...prevItems]
                const deletedItems = newItems.splice(source.index, 1)
                info('newItems', {
                    prevItems,
                    combine,
                    newItems,
                    deletedItems,
                })
                return newItems
            })
            return
        }
    }
    info('PlaceStms RENDER', allPlaced, items2place.length, items.length)
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            {/* <Snackbar
                anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
                autoHideDuration={500}
                open={!!snackTxt}
                onClose={() => setSnackTxt('')}
                message={snackTxt}
                key={'bottomcenter'}
            /> */}
            <div className="flex flex-1 flex-col justify-start items-center my-0">
                {/* <Typography
                    variant="h5"
                    className="text-center text-white font-bold italic w-11/12 bg-blue-500"
                >
                    Où placeriez-vous l'énoncé suivant ?
                </Typography> */}
                {/* {!allPlaced && ( */}
                <Droppable droppableId="droppable-source">
                    {(provided: any, _) => (
                        <div
                            className="flex flex-col justify-center items-center w-[100%]"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {items2place.map((data, index) => (
                                <Draggable
                                    key={`src-${data.id}`}
                                    draggableId={`src-${data.id}`}
                                    index={index}
                                >
                                    {(provided, _) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                // some basic styles to make the items look a bit nicer
                                                userSelect: 'none',
                                                // padding: grid * 2,
                                                // margin: `0 0 ${grid}px 0`,
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                // backgroundColor: 'pink',
                                                ...provided.draggableProps
                                                    .style,
                                            }}
                                        >
                                            <StatementCard
                                                showId={params.assessmentParams['debug_mode'] === 1}
                                                big
                                                textSize='h6'
                                                highlight
                                                emoji={
                                                    !!params.assessmentParams[
                                                        'values_with_icons'
                                                    ]
                                                }
                                                style={{
                                                    width: '100%',
                                                    backgroundColor:
                                                        'lightblue',
                                                }}
                                                {...data}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
                {/* )} */}

                <Divider flexItem className='mb-1'>
                   {t('hint_place_below')}
                </Divider>
                {/*  */}
                <ScaleHint type='BEST' />
                {items.map((stm, index) =>
                    stm ? (
                        <StatementCard
                            showId={params.assessmentParams['debug_mode'] === 1}
                            big
                            textSize='h6'
                            emoji={!!params.assessmentParams['values_with_icons']}
                            highlight={stm.id === p.stmId2Highlight}
                            {...stm}
                            key={stm.id}
                            style={{
                                width: '100%',
                                backgroundColor: 'lightgrey',
                                marginBottom: '1rem',
                            }}
                            index={index}
                        />
                    ) : (
                        <Droppable
                            // isCombineEnabled
                            droppableId={`placeholder-${index}`}
                            key={`placeholder-${index}`}
                        >
                            {(provided, _) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={{
                                        width: '90%',
                                        backgroundColor: 'white',
                                    }}
                                    // className="min-w-fit m-1 border-2 border-dashed border-gray-400 h-8 flex justify-center items-center"
                                >
                                    {/* <div
                                        title={`placeholder-${index}`}
                                        className="text-gray-400 text-center"
                                    >
                                        Drop here
                                    </div> */}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )
                )}
                {/*  */}
                <ScaleHint type='WORST' />

                
            </div>
        </DragDropContext>
    )
}
